import gsap from "gsap/all";

export default () => ({
  mouseenterAnimation: null,
  open: false,
  selectedTags: [],
  selectedCategories: [],
  tags: [],
  btnHoverColor: getComputedStyle(document.body).getPropertyValue(
    "--btn-hover"
  ),

  init() {
    this.initAnimation();
  },

  initAnimation() {
    const inBackend = document.body.classList.contains("neos-backend");

    if (inBackend) return;

    const teaserItems = document.querySelectorAll(".teasers .teaser");

    teaserItems.forEach((teaser) => {
      gsap
        .timeline({
          scrollTrigger: {
            trigger: teaser,
            start: "top 70%",
          },
        })
        .to(teaser.querySelectorAll(".slide-in-left-md"), {
          xPercent: 75,
          stagger: 0.05,
          opacity: 1,
          duration: 0.6,
          onComplete: function () {
            // clear transform to maintain responsiveness of element
            this.targets().forEach((el) => {
              el.classList.remove("slide-in-left-md");
              el.style.removeProperty("transform");
            });
          },
        })
        .to(
          teaser.querySelectorAll(".button-link"),
          {
            opacity: 1,
            scale: 1,
            duration: 0.25,
          },
          "-=0.4"
        );
    });
  },

  filtersInit(items) {
    const tagsWithoutCategories = [
      ...new Set(items.flatMap((item) => item.tags.map((tag) => tag.title))),
    ];

    this.tags = tagsWithoutCategories.map((tag) => {
      const categories = items
        .filter((item) => item.tags.map((t) => t.title).includes(tag))
        .map((item) => item.subtitle)
        .filter((item) => item);

      const uniqCategories = [...new Set(categories)];

      return {
        name: tag[0] === "#" ? tag.slice(1) : tag,
        categories: uniqCategories,
      };
    });
  },

  teaserVisibility(teaser) {
    return {
      ["x-bind:class"]() {
        const { tags, subtitle: category } = teaser;
        let isTeaserVisible;
        const isTeaserSelectedInTags = tags
          .map((tag) => ({ name: tag.title }))
          .some((tag) => this.isSelectedTag(tag));
        const isTeaserSelectedInCategories = this.isSelectedCategory(category);

        if (
          this.selectedTags.length > 0 &&
          this.selectedCategories.length > 0
        ) {
          isTeaserVisible =
            isTeaserSelectedInTags && isTeaserSelectedInCategories;

          return isTeaserVisible ? "" : "hidden";
        } else if (
          this.selectedTags.length > 0 ||
          this.selectedCategories.length > 0
        ) {
          isTeaserVisible =
            isTeaserSelectedInTags || isTeaserSelectedInCategories;

          return isTeaserVisible ? "" : "hidden";
        } else {
          return "";
        }
      },
    };
  },

  button: {
    ["@mouseenter.self"](event) {
      this.mouseenterAnimation = gsap.timeline().to(
        event.target.firstChild,
        {
          borderRadius: "100%",
          backgroundColor: this.btnHoverColor,
          scale: 0.85,
          duration: 0.4,
          delay: 0.1,
        },
        0
      );
    },
    ["@mouseleave.self"](event) {
      this.mouseenterAnimation.clear();
      this.mouseenterAnimation.kill();

      gsap.timeline().to(
        event.target.firstChild,
        {
          borderRadius: 0,
          backgroundColor: "#FFA01E",
          scale: 1,
          duration: 0.3,
        },
        0
      );
    },
  },

  openModal: {
    ["@click"]() {
      this.open = !this.open;
    },
  },

  showModal: {
    ["x-show"]() {
      return this.open;
    },
    ["x-bind:class"]() {
      return this.open ? "active" : "";
    },
  },

  getUniqCategories() {
    const flatCategories = this.tags.flatMap((tag) => tag.categories);
    return Array.from(new Set(flatCategories));
  },

  getMostCommonCategories() {
    const uniqCategories = this.getUniqCategories();
    const categoriesWithCount = uniqCategories.map((category) => {
      const count = this.tags.reduce((currentCount, currentTag) => {
        const isCategoryInTag = currentTag.categories.includes(category);
        return isCategoryInTag ? currentCount + 1 : currentCount;
      }, 0);

      return {
        name: category,
        count,
      };
    });

    categoriesWithCount.sort((a, b) => b.count - a.count);

    return categoriesWithCount.map((category) => category.name);
  },

  toggleTag(tag) {
    if (typeof tag === "string") {
      tag = this.tags.find((t) => t.name === tag);
    }
    const isAlreadySelected = this.isSelectedTag(tag);

    if (isAlreadySelected) {
      this.selectedTags = this.selectedTags.filter((t) => t.name !== tag.name);
      return;
    }

    this.selectedTags.push(tag);
  },

  toggleCategory(category) {
    const isAlreadySelected = this.isSelectedCategory(category);

    if (isAlreadySelected) {
      this.selectedCategories = this.selectedCategories.filter(
        (c) => c !== category
      );
      return;
    }

    this.selectedCategories.push(category);
  },

  isSelectedTag(tag) {
    if (typeof tag === "string") {
      tag = this.tags.find((t) => t.name === tag);
    }
    return this.selectedTags.some((t) => t.name === tag.name.replace("#", ""));
  },

  isSelectedCategory(category) {
    return this.selectedCategories.includes(category);
  },

  isTagDisabled(tag) {
    const isTagInSelectedCategories = this.selectedCategories.some((category) =>
      tag.categories.includes(category)
    );
    return this.selectedCategories.length && !isTagInSelectedCategories;
  },

  isCategoryDisabled(category) {
    const isCategoryInSelectedTags = this.selectedTags.some((tag) =>
      tag.categories.includes(category)
    );
    return this.selectedTags.length && !isCategoryInSelectedTags;
  },

  resetFilters() {
    this.selectedTags = [];
    this.selectedCategories = [];
  },
});
